import React from 'react';
import { Box, Typography, Paper, Grid, Alert, Divider, List, ListItem, ListItemIcon, ListItemText, Chip } from '@mui/material';
import {
  Notifications as NotificationsIcon,
  EmojiEvents as WinIcon,
  Dashboard as BoardIcon,
  Campaign as SystemIcon,
  PhoneIphone as MobileIcon,
  Computer as DesktopIcon,
  CheckCircle as CheckIcon,
  NotificationsActive as BellIcon,
  DoneAll as ReadIcon,
  FilterList as FilterIcon,
} from '@mui/icons-material';
import { SupportLayout, InfoCard, VisualExample } from '../../components/support';
import { useDocumentTitle } from '../../hooks/core';

export const NotificationsSupportPage: React.FC = () => {
  useDocumentTitle('Notifications - Support');

  return (
    <SupportLayout
      title="Notifications"
      description="Stay informed about your wins, board updates, and system announcements with real-time notifications."
      breadcrumbs={[{ label: 'Notifications' }]}
    >
      {/* Overview */}
      <Box id="notifications-overview" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <NotificationsIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Notification Overview
        </Typography>

        <Typography variant="body1" paragraph>
          SquareUp keeps you informed with <strong>real-time notifications</strong> about important events on your boards. You'll know instantly when you win, when your squares are approved, and when boards you're participating in have updates.
        </Typography>

        <Paper elevation={0} sx={{ p: 4, bgcolor: 'primary.50', border: 1, borderColor: 'primary.200' }}>
          <Grid container spacing={4} alignItems="center">
            <Grid size={{ xs: 12, md: 6 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <BellIcon sx={{ fontSize: 48, color: 'primary.main' }} />
                <Box>
                  <Typography variant="h4" fontWeight="bold" color="primary.main">Real-Time</Typography>
                  <Typography variant="body2" color="text.secondary">Instant Updates</Typography>
                </Box>
              </Box>
              <Typography variant="body1">
                Notifications are delivered instantly via Server-Sent Events (SSE). No need to refresh the page—you'll see the notification badge update as soon as something happens.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Paper sx={{ p: 3, bgcolor: 'white' }}>
                <Typography variant="h6" gutterBottom>What You'll Be Notified About</Typography>
                <List dense>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}><WinIcon fontSize="small" sx={{ color: '#FFD700' }} /></ListItemIcon>
                    <ListItemText primary="Wins from any board you're on" />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                    <ListItemText primary="Square requests approved" />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}><BoardIcon fontSize="small" color="primary" /></ListItemIcon>
                    <ListItemText primary="Board status changes" />
                  </ListItem>
                  <ListItem disablePadding sx={{ py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}><SystemIcon fontSize="small" color="info" /></ListItemIcon>
                    <ListItemText primary="System announcements" />
                  </ListItem>
                </List>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Notification Types */}
      <Box id="notification-types" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <FilterIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Notification Types
        </Typography>

        <Typography variant="body1" paragraph>
          Notifications are grouped into three categories to help you find what you're looking for:
        </Typography>

        <Grid container spacing={3}>
          {/* Wins */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={0} sx={{ p: 3, border: 2, borderColor: '#FFD700', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <WinIcon sx={{ color: '#FFD700', fontSize: 28 }} />
                <Typography variant="h6" fontWeight="bold">Wins</Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Get notified whenever you win money on a board. This includes:
              </Typography>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemText
                    primary="Main quarter wins"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemText
                    primary="Wrong Way / Reverse wins"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemText
                    primary="+N bonus wins"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemText
                    primary="Neighbor payouts"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              </List>
              <Alert severity="info" sx={{ mt: 2, py: 0.5 }}>
                <Typography variant="caption">
                  Multiple wins from the same quarter are combined into one notification showing your total.
                </Typography>
              </Alert>
            </Paper>
          </Grid>

          {/* Board Updates */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={0} sx={{ p: 3, border: 2, borderColor: 'primary.main', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <BoardIcon sx={{ color: 'primary.main', fontSize: 28 }} />
                <Typography variant="h6" fontWeight="bold">Board Updates</Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Stay informed about boards you're participating in:
              </Typography>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemText
                    primary="Square requests approved/denied"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemText
                    primary="Board published (went live)"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemText
                    primary="Numbers randomized"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemText
                    primary="Board locked/completed"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>

          {/* System */}
          <Grid size={{ xs: 12, md: 4 }}>
            <Paper elevation={0} sx={{ p: 3, border: 2, borderColor: 'info.main', height: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <SystemIcon sx={{ color: 'info.main', fontSize: 28 }} />
                <Typography variant="h6" fontWeight="bold">System</Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Important announcements from SquareUp:
              </Typography>
              <List dense disablePadding>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemText
                    primary="New features announced"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemText
                    primary="Promotions & offers"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemText
                    primary="New themes available"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemText
                    primary="General announcements"
                    primaryTypographyProps={{ variant: 'body2' }}
                  />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Accessing Notifications */}
      <Box id="accessing-notifications" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Accessing Your Notifications
        </Typography>

        <Typography variant="body1" paragraph>
          Access notifications from the bell icon in the navigation bar. The badge shows your unread count.
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <DesktopIcon color="primary" />
                <Typography variant="h6" fontWeight="bold">Desktop</Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Click the bell icon to open a dropdown showing your 5 most recent notifications. Use "View all notifications" to see the full list with filtering options.
              </Typography>
              <List dense>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Quick preview dropdown" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Scrollable notification list" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Mark all as read button" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              </List>
            </Paper>
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <Paper elevation={0} sx={{ p: 3, border: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                <MobileIcon color="secondary" />
                <Typography variant="h6" fontWeight="bold">Mobile</Typography>
              </Box>
              <Typography variant="body2" paragraph>
                Tap the bell to open a full-screen drawer with all your notifications. Swipe or use the close button to dismiss.
              </Typography>
              <List dense>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Full-screen notification drawer" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Load more pagination" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
                <ListItem disablePadding sx={{ py: 0.25 }}>
                  <ListItemIcon sx={{ minWidth: 28 }}><CheckIcon fontSize="small" color="success" /></ListItemIcon>
                  <ListItemText primary="Optimized for touch" primaryTypographyProps={{ variant: 'body2' }} />
                </ListItem>
              </List>
            </Paper>
          </Grid>
        </Grid>

        <InfoCard title="Notification Bell Badge" variant="tip" icon={<BellIcon />}>
          <Typography variant="body2">
            The red badge on the bell shows your unread count. It caps at "99+" if you have more than 99 unread notifications. The bell also has a subtle pulse animation when you have unread items.
          </Typography>
        </InfoCard>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Managing Notifications */}
      <Box id="managing-notifications" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <ReadIcon sx={{ verticalAlign: 'middle', mr: 1 }} />
          Managing Notifications
        </Typography>

        <Typography variant="body1" paragraph>
          Keep your notifications organized with these actions:
        </Typography>

        <Grid container spacing={3}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', textAlign: 'center', height: '100%' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Mark as Read</Typography>
              <Typography variant="caption" color="text.secondary">
                Click any notification to mark it as read and navigate to the related page
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', textAlign: 'center', height: '100%' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Mark All Read</Typography>
              <Typography variant="caption" color="text.secondary">
                Use the "Mark all read" button to clear all unread notifications at once
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', textAlign: 'center', height: '100%' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>Filter by Category</Typography>
              <Typography variant="caption" color="text.secondary">
                On the notifications page, filter by Wins, Board Updates, or System
              </Typography>
            </Paper>
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', textAlign: 'center', height: '100%' }}>
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>View Board</Typography>
              <Typography variant="caption" color="text.secondary">
                Each notification has a "View Board" button to jump directly to the relevant board
              </Typography>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* Win Notifications Detail */}
      <Box id="win-notifications" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          <WinIcon sx={{ verticalAlign: 'middle', mr: 1, color: '#FFD700' }} />
          Win Notification Details
        </Typography>

        <Typography variant="body1" paragraph>
          Win notifications tell you exactly how you won and how much. If you won multiple payouts from the same quarter (e.g., main + neighbor), they're combined into one notification.
        </Typography>

        <VisualExample title="Single Win Notification" caption="When you win one payout from a quarter">
          <Paper elevation={1} sx={{ p: 2, maxWidth: 400, mx: 'auto' }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <WinIcon sx={{ color: '#FFD700', fontSize: 32 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">You won $25.00!</Typography>
                <Typography variant="body2" color="text.secondary">
                  Congratulations! You won Q2 on Super Bowl Pool
                </Typography>
                <Typography variant="body2" color="success.main" fontWeight="bold" sx={{ mt: 0.5 }}>
                  +$25.00
                </Typography>
              </Box>
            </Box>
          </Paper>
        </VisualExample>

        <VisualExample title="Combined Win Notification" caption="When you win multiple payouts from one quarter">
          <Paper elevation={1} sx={{ p: 2, maxWidth: 400, mx: 'auto' }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <WinIcon sx={{ color: '#FFD700', fontSize: 32 }} />
              <Box>
                <Typography variant="subtitle1" fontWeight="bold">You won $45.00!</Typography>
                <Typography variant="body2" color="text.secondary">
                  You won $25.00 (main) and $20.00 (neighbor) on Super Bowl Pool (Q2)
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.5, mt: 0.5, flexWrap: 'wrap' }}>
                  <Chip label="Main: $25.00" size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'success.light', color: 'success.contrastText' }} />
                  <Chip label="Neighbor: $20.00" size="small" sx={{ height: 20, fontSize: '0.7rem', bgcolor: 'grey.200' }} />
                </Box>
                <Typography variant="body2" color="success.main" fontWeight="bold" sx={{ mt: 0.5 }}>
                  +$45.00
                </Typography>
              </Box>
            </Box>
          </Paper>
        </VisualExample>

        <Alert severity="success" sx={{ mt: 3 }}>
          <strong>Win Types:</strong> Main, Wrong Way, +N, +N Wrong Way, Neighbor, and Box Neighbor wins are all tracked separately so you know exactly how you won.
        </Alert>
      </Box>

      <Divider sx={{ my: 4 }} />

      {/* FAQ */}
      <Box id="notifications-faq" sx={{ mb: 5 }}>
        <Typography variant="h5" gutterBottom fontWeight="bold">
          Common Questions
        </Typography>

        <Grid container spacing={2}>
          {[
            {
              q: 'Why am I not receiving win notifications?',
              a: 'Win notifications are only sent to registered users with claimed squares. Offline players and unclaimed squares do not receive notifications.',
            },
            {
              q: 'Can I turn off notifications?',
              a: 'Currently all in-app notifications are enabled. Email notification preferences can be managed in your profile settings.',
            },
            {
              q: 'How long are notifications kept?',
              a: 'Notifications are kept indefinitely. You can view your entire notification history on the notifications page.',
            },
            {
              q: 'Do I get notified for boards I manage?',
              a: 'Yes! Board managers also receive notifications when they need to approve square requests from players.',
            },
          ].map((item, index) => (
            <Grid size={{ xs: 12, md: 6 }} key={index}>
              <Paper elevation={0} sx={{ p: 2, border: 1, borderColor: 'divider', height: '100%' }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  {item.q}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {item.a}
                </Typography>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Box>
    </SupportLayout>
  );
};

export default NotificationsSupportPage;
